import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { RedWordMatch } from '@/shared/utils/redWords';
import { cn } from '@/shared/lib/utils';

interface RedWordConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  redWordMatches: RedWordMatch[];
  onConfirm: () => void;
  onCancel: () => void;
  onFixForMe?: (originalDescription: string) => Promise<void>;
  originalDescription: string;
  darkMode?: boolean;
  action: 'save' | 'add'; // Whether this is saving or adding an entry
  isFixing?: boolean;
}

const RedWordConfirmationDialog: React.FC<RedWordConfirmationDialogProps> = ({
  open,
  onOpenChange,
  redWordMatches,
  onConfirm,
  onCancel,
  onFixForMe,
  originalDescription,
  darkMode = false,
  action = 'save',
  isFixing = false,
}) => {
  const actionTextCapitalized = action === 'save' ? 'Save' : 'Add';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "sm:max-w-[600px]",
          darkMode ? "text-[#abb2bf] bg-[#21252b] border-[#3e4451]" : ""
        )}
      >
        <DialogHeader>
          <DialogTitle className="text-red-600 dark:text-red-400">
            Red Words Detected
          </DialogTitle>
          <DialogDescription>
            Your entry contains {redWordMatches.length} red word{redWordMatches.length !== 1 ? 's' : ''} that should be avoided. 
            Review the suggestions below and consider replacing them before proceeding.
          </DialogDescription>
        </DialogHeader>
        
        <div className="max-h-[400px] overflow-y-auto space-y-3 py-4">
          {redWordMatches.map((match, index) => (
            <div
              key={index}
              className={cn(
                "p-3 rounded-lg border",
                darkMode 
                  ? "bg-[#3e4451] border-[#3e4451]" 
                  : "bg-red-50 border-red-200"
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="font-medium text-red-600 dark:text-red-400 mb-1">
                    "{match.word}"
                  </div>
                  <div className={cn(
                    "text-sm",
                    darkMode ? "text-[#abb2bf]" : "text-gray-700"
                  )}>
                    <strong>Suggested alternatives:</strong> {match.suggestion}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2 sm:gap-2 flex-col-reverse sm:flex-row">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isFixing}
            className={cn(
              darkMode
                ? "text-[#abb2bf] bg-[#21252b] border-[#3e4451] hover:bg-[#3e4451] hover:text-[#abb2bf]"
                : ""
            )}
          >
            Cancel
          </Button>
          {onFixForMe && (
            <Button
              onClick={() => onFixForMe(originalDescription)}
              disabled={isFixing}
              className={cn(
                "bg-blue-600 hover:bg-blue-700 text-white",
                darkMode
                  ? "bg-blue-600 hover:bg-blue-700"
                  : ""
              )}
            >
              {isFixing ? 'Fixing...' : 'Fix for me'}
            </Button>
          )}
          <Button
            onClick={onConfirm}
            disabled={isFixing}
            className={cn(
              "bg-red-600 hover:bg-red-700 text-white",
              darkMode
                ? "bg-red-600 hover:bg-red-700"
                : ""
            )}
          >
            {actionTextCapitalized} Anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RedWordConfirmationDialog;
